import { useCallback, useEffect, useId, useState, useMemo, type FC } from "react";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import type { PlainWorkflow } from "@/queries/automation-queries";
import { WorkflowEditAction } from "@/components/automations/AutomationEditAction";
import { WorkflowEditTriggers } from "@/components/automations/AutomationEditTriggers";
import { WorkflowEditExecutor } from "@/components/automations/AutomationEditExecutor";
import {
    convertTriggersToFormData,
    convertWorkflowActionToFormSteps,
    convertWorkflowActionToFormLimits,
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormData,
    type WorkflowEditFormStepData,
    type WorkflowEditFormTriggerData,
    type WorkflowEditFormLimitsData,
} from "@/components/automations/automation-edit-form-data";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";

export const WorkflowEditForm: FC<{
    workflow: PlainWorkflow;
    formName: string;
    onDisabledChange: (disabled: boolean) => void;
    onSubmit: (values: WorkflowEditFormData) => void;
}> = ({ workflow, formName, onSubmit, onDisabledChange }) => {
    const nameId = useId();
    const descriptionId = useId();
    const executorId = useId();

    const [name, setName] = useState<string>(workflow.metadata?.name || "");
    const [description, setDescription] = useState<string>(workflow.metadata?.description || "");
    const [executor, setExecutor] = useState<PlainMessage<Subject> | undefined>(workflow.metadata?.executor);
    const [steps] = useState<WorkflowEditFormStepData[]>(() => convertWorkflowActionToFormSteps(workflow.spec?.action));
    const [triggers, setTriggers] = useState<WorkflowEditFormTriggerData[]>(() =>
        convertTriggersToFormData(workflow.spec?.triggers ?? []),
    );
    const [limits, setLimits] = useState<WorkflowEditFormLimitsData | undefined>(() =>
        convertWorkflowActionToFormLimits(workflow.spec?.action),
    );

    const isFormValid = useMemo(() => {
        const nameValid = name.trim().length > 0 && name.trim().length <= 80;

        // At least one step required
        const hasAtLeastOneStep = steps.length > 0;

        // All steps must have content
        const stepsValid = steps.every((step) => {
            switch (step.type) {
                case "command":
                    return step.content.trim().length > 0;
                case "prompt":
                    return step.content.trim().length > 0;
                case "pr":
                    return step.title.trim().length > 0 && step.branch.trim().length > 0;
            }
        });

        // At least one trigger required
        const hasAtLeastOneTrigger = triggers.length > 0;

        // All manual triggers must have at least one project
        const allManualTriggersValid = triggers.every((trigger) => {
            if (trigger.type === "manual") {
                switch (trigger.context?.type) {
                    case WorkflowEditFormTriggerContextType.Projects:
                        return trigger.context.projectIds.length > 0;
                    case WorkflowEditFormTriggerContextType.Repositories:
                        return trigger.context.repoSearchString.length > 0;
                    default:
                        // Other context types  (pr, env) - validate when implemented
                        return true;
                }
            }
            // Other trigger types (time, pullRequest) - validate when implemented
            return true;
        });

        return nameValid && hasAtLeastOneStep && stepsValid && hasAtLeastOneTrigger && allManualTriggersValid;
    }, [name, steps, triggers]);

    const originalSteps = convertWorkflowActionToFormSteps(workflow.spec?.action);

    const originalTriggers = useMemo(
        () => convertTriggersToFormData(workflow.spec?.triggers),
        [workflow.spec?.triggers],
    );

    const originalLimits = useMemo(
        () => convertWorkflowActionToFormLimits(workflow.spec?.action),
        [workflow.spec?.action],
    );

    const isFormChanged = useMemo(() => {
        const nameChanged = name !== (workflow.metadata?.name || "");
        const descriptionChanged = description !== (workflow.metadata?.description || "");
        const executorChanged = JSON.stringify(executor) !== JSON.stringify(workflow.metadata?.executor);
        const triggersChanged = JSON.stringify(triggers) !== JSON.stringify(originalTriggers);
        const stepsChanged = JSON.stringify(steps) !== JSON.stringify(originalSteps);
        const limitsChanged = JSON.stringify(limits) !== JSON.stringify(originalLimits);

        return nameChanged || descriptionChanged || executorChanged || triggersChanged || stepsChanged || limitsChanged;
    }, [
        name,
        workflow.metadata?.name,
        workflow.metadata?.description,
        description,
        executor,
        workflow.metadata?.executor,
        triggers,
        originalTriggers,
        steps,
        originalSteps,
        limits,
        originalLimits,
    ]);

    const isDisabled = !isFormValid || !isFormChanged;

    useEffect(() => {
        onDisabledChange(isDisabled);
    }, [isDisabled, onDisabledChange]);

    const handleSubmit = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            if (isDisabled) return;
            onSubmit({
                name: name.trim(),
                description: description.trim(),
                executor,
                steps: steps,
                triggers,
                limits,
            });
        },
        [isDisabled, name, description, executor, steps, onSubmit, triggers, limits],
    );

    return (
        <form id={formName} name={formName} onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                <InputField label="Name" id={nameId}>
                    <Input
                        id={nameId}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Name"
                        minLength={1}
                        maxLength={80}
                        required
                        className="max-w-[700px] bg-transparent"
                        data-1p-ignore
                        data-lpignore
                        data-protonpass-ignore
                    />
                </InputField>

                <InputField label="Description" id={descriptionId}>
                    <Input
                        id={descriptionId}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Description"
                        className="max-w-[700px] bg-transparent"
                        maxLength={500}
                    />
                </InputField>

                <InputField
                    label="Run as"
                    hint="Select who runs this automation, you or a service account"
                    id={executorId}
                >
                    <WorkflowEditExecutor
                        selectedExecutorId={executor?.id}
                        onExecutorChange={setExecutor}
                        className="w-[250px] min-w-fit max-w-[700px]"
                    />
                </InputField>
            </div>
            <WorkflowEditTriggers workflow={workflow} triggers={triggers} onTriggersChange={setTriggers} />
            <WorkflowEditAction limits={limits} onLimitsChange={setLimits} />
        </form>
    );
};
